import { useEffect, useMemo, useRef, useState } from "react";
import type { RoundName } from "../../domain/round/types";
import { roundApi, type TeeTimesSource } from "../../lib/roundApi";
import { diagnoseDpWorld, fetchDpWorldPreview, type DpWorldNetworkDiagnostics, type DpWorldPreview } from "./dpworldApi";
import { buildEmergencyPreview, clearCachedPreview, loadCachedPreview, saveCachedPreview } from "./resilientSchedule";
import "./dpworld-tee-times.css";

type Props = {
  tournamentId: string;
  rounds: RoundName[];
  onApply: (round: RoundName, preview: DpWorldPreview) => void;
  onClear: (round: RoundName) => void;
};

function responseSummary(diag: DpWorldNetworkDiagnostics | null) {
  if (!diag) return "";
  return diag.responses.map((item) => `${item.mode || "source"}: ${item.error || item.status || "?"}`).join(" · ");
}

function validOfficialUrl(value: string) {
  return /^https:\/\/(?:www\.)?europeantour\.com\//i.test(value.trim()) && /\/tee-times/i.test(value);
}

export function DpWorldTeeTimesPanel({ tournamentId, rounds, onApply, onClear }: Props) {
  const [source, setSource] = useState<TeeTimesSource | null>(null);
  const [url, setUrl] = useState("");
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [expectedCount, setExpectedCount] = useState(0);
  const [previews, setPreviews] = useState<Partial<Record<RoundName, DpWorldPreview>>>({});
  const [networkDiagnostics, setNetworkDiagnostics] = useState<Partial<Record<RoundName, DpWorldNetworkDiagnostics>>>({});
  const [busyRound, setBusyRound] = useState<RoundName | "">("");
  const [diagnosingRound, setDiagnosingRound] = useState<RoundName | "">("");
  const [sourceBusy, setSourceBusy] = useState(false);
  const [error, setError] = useState("");
  const [emergencyGroupCount, setEmergencyGroupCount] = useState(24);
  const [emergencyTwoTee, setEmergencyTwoTee] = useState(true);
  const [emergencyTee10Count, setEmergencyTee10Count] = useState(12);
  const [emergencyPlayers, setEmergencyPlayers] = useState<2 | 3>(3);
  const autoToken = useRef(0);

  const validUrl = useMemo(() => validOfficialUrl(url), [url]);
  const roundsKey = rounds.join("|");

  async function load(round: RoundName, explicitUrl = url.trim()) {
    if (!validOfficialUrl(explicitUrl)) return;
    setBusyRound(round);
    setError("");
    try {
      const result = await fetchDpWorldPreview(explicitUrl, round, expectedCount);
      const cached = { ...result, cachedAt: new Date().toISOString() };
      setPreviews((value) => ({ ...value, [round]: cached }));
      setNetworkDiagnostics((value) => ({ ...value, [round]: undefined }));
      saveCachedPreview(tournamentId, round, cached);
      onApply(round, cached);
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    } finally {
      setBusyRound("");
    }
  }

  useEffect(() => {
    if (!tournamentId || !rounds.length) return;
    const token = ++autoToken.current;
    setSourceBusy(true);
    setSource(null);
    setUrl("");
    setError("");
    setNetworkDiagnostics({});

    // Restore only Tee Times cached for this exact tournament + round.
    // If this scope has no cache, explicitly clear the parent generator state;
    // otherwise groups from the previously selected tournament/round can leak
    // into the new view and even be sent to a generation request.
    const restored: Partial<Record<RoundName, DpWorldPreview>> = {};
    for (const round of rounds) {
      const cached = loadCachedPreview(tournamentId, round);
      if (cached) {
        restored[round] = cached;
        onApply(round, cached);
      } else {
        onClear(round);
      }
    }
    setPreviews(restored);

    void roundApi.teeTimesSource(tournamentId, rounds[0]).then((resolved) => {
      if (token !== autoToken.current) return;
      setSource(resolved);
      setUrl(resolved.url || resolved.savedUrl || resolved.derivedUrl || "");
      if (!validOfficialUrl(resolved.url || "")) setOverrideOpen(true);
    }).catch((exc) => {
      if (token !== autoToken.current) return;
      setError(exc instanceof Error ? exc.message : String(exc));
      setOverrideOpen(true);
    }).finally(() => {
      if (token === autoToken.current) setSourceBusy(false);
    });
  // roundsKey intentionally represents the requested round set.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId, roundsKey]);

  async function diagnose(round: RoundName) {
    setDiagnosingRound(round);
    setError("");
    try {
      const result = await diagnoseDpWorld(url.trim(), round);
      setNetworkDiagnostics((value) => ({ ...value, [round]: result }));
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    } finally {
      setDiagnosingRound("");
    }
  }

  async function saveOverride() {
    if (!source || !tournamentId) return;
    setSourceBusy(true);
    setError("");
    try {
      const saved = await roundApi.saveTeeTimesSource(tournamentId, source.configRevision, url.trim());
      setSource(saved);
      setUrl(saved.url || saved.savedUrl || saved.derivedUrl || "");
      setOverrideOpen(false);
      // Saving a source URL is configuration only. A remote fetch is always an
      // explicit operator action so it cannot overwrite a verified schedule.
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    } finally {
      setSourceBusy(false);
    }
  }

  async function restoreAuto() {
    if (!source || !tournamentId) return;
    setSourceBusy(true);
    setError("");
    try {
      const saved = await roundApi.saveTeeTimesSource(tournamentId, source.configRevision, "");
      setSource(saved);
      setUrl(saved.url || saved.derivedUrl || "");
      setOverrideOpen(false);
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    } finally {
      setSourceBusy(false);
    }
  }

  function createEmergency(round: RoundName) {
    const count = Math.max(1, Math.min(80, Math.round(emergencyGroupCount || 1)));
    const tee10Count = emergencyTwoTee
      ? Math.max(1, Math.min(count - 1, Math.round(emergencyTee10Count || Math.floor(count / 2))))
      : 0;
    const preview = buildEmergencyPreview(round, {
      groupCount: count,
      twoTee: emergencyTwoTee,
      tee10Count,
      playersPerGroup: emergencyPlayers,
    });
    setPreviews((value) => ({ ...value, [round]: preview }));
    setNetworkDiagnostics((value) => ({ ...value, [round]: undefined }));
    saveCachedPreview(tournamentId, round, preview);
    onApply(round, preview);
    setError("");
  }

  function clear(round: RoundName) {
    setPreviews((value) => ({ ...value, [round]: undefined }));
    setNetworkDiagnostics((value) => ({ ...value, [round]: undefined }));
    clearCachedPreview(tournamentId, round);
    onClear(round);
  }

  return (
    <div className="dpwt-panel">
      <div className="dpwt-auto-source">
        <div>
          <span>DP WORLD TOUR · ŹRÓDŁO TURNIEJU</span>
          <strong>{sourceBusy ? "Sprawdzam źródło…" : source?.sourceMode === "saved" ? "Zapisany link" : source?.sourceMode === "derived" ? "Link wyliczony automatycznie" : "Brak automatycznego źródła"}</strong>
          <small>{source?.url || url || "Ustaw awaryjny link poniżej."}</small>
        </div>
        <button type="button" className="text-button" onClick={() => setOverrideOpen((value) => !value)}>{overrideOpen ? "Ukryj ustawienia" : "Awaryjny link / ustawienia"}</button>
      </div>

      {overrideOpen ? (
        <div className="dpwt-source-row">
          <label className="dpwt-url"><span>Override URL dla tego turnieju</span><input type="url" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://www.europeantour.com/.../tee-times" /></label>
          <label className="dpwt-expected"><span>Oczekiwana liczba grup</span><input type="number" min={0} max={200} value={expectedCount || ""} onChange={(event) => setExpectedCount(Math.max(0, Number(event.target.value) || 0))} placeholder="auto" /></label>
          <div className="dpwt-source-actions">
            <button type="button" className="secondary-button" disabled={sourceBusy || Boolean(url.trim() && !validUrl)} onClick={() => void saveOverride()}>Zapisz dla turnieju</button>
            {source?.hasOverride ? <button type="button" className="text-button" disabled={sourceBusy} onClick={() => void restoreAuto()}>Przywróć AUTO</button> : null}
          </div>
        </div>
      ) : null}

      <p className="dpwt-help"><strong>Bez automatycznego resetu:</strong> po pobraniu plan jest zapamiętany dla turnieju i rundy. DP World jest sprawdzany ponownie dopiero po kliknięciu „Odśwież z DP World”. Godzina 00:00 jest traktowana jako grupa przełożona, nie jako północ.</p>
      {error ? <div className="dpwt-error">{error}</div> : null}
      <div className="dpwt-rounds">
        {rounds.map((round) => {
          const preview = previews[round];
          const diagnostics = preview?.diagnostics;
          const network = networkDiagnostics[round] || null;
          const postponedCount = diagnostics?.postponedCount ?? 0;
          return (
            <section key={round} className={`dpwt-round ${diagnostics?.complete ? "complete" : ""}`}>
              <header><div><strong>{round}</strong><span>{preview ? `${preview.count} grup` : "brak zapisanych tee times"}</span></div>{diagnostics?.complete ? <b>{preview?.emergency ? "AWARYJNY" : "✓ GOTOWE"}</b> : null}</header>
              {!preview ? (
                <div className="dpwt-round-empty">
                  <button type="button" className="secondary-button" disabled={!validUrl || Boolean(busyRound)} onClick={() => void load(round, url.trim())}>{busyRound === round ? "Pobieram…" : `Pobierz ${round} z DP World`}</button>
                  <button type="button" className="text-button" disabled={!validUrl || Boolean(diagnosingRound)} onClick={() => void diagnose(round)}>{diagnosingRound === round ? "Sprawdzam…" : "Diagnostyka"}</button>
                </div>
              ) : (
                <>
                  <div className="dpwt-kpis">
                    <span><small>Grupy</small><strong>{diagnostics?.groupCount ?? preview.count}{diagnostics?.expectedCount ? ` / ${diagnostics.expectedCount}` : ""}</strong></span>
                    <span><small>Zawodnicy</small><strong>{diagnostics?.playerCount ?? "—"}</strong></span>
                    <span><small>Tee 1</small><strong>{diagnostics?.teeCounts?.["1"] ?? 0}</strong></span>
                    <span><small>Tee 10</small><strong>{diagnostics?.teeCounts?.["10"] ?? 0}</strong></span>
                    {postponedCount ? <span><small>Przełożone / bez godziny</small><strong>{postponedCount}</strong></span> : null}
                  </div>
                  <div className="dpwt-source-mode">{diagnostics?.sourceMode || "DP World Tour"} · parser {diagnostics?.parserVersion || "—"}{preview.cachedAt ? " · zapisane lokalnie" : ""}</div>
                  {diagnostics?.warnings?.length ? <ul className="dpwt-warnings">{diagnostics.warnings.map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}</ul> : null}
                  <div className="dpwt-actions">
                    <span className="gen-success">✓ Ten plan pozostaje zapisany przy ponownym wejściu</span>
                    <button type="button" className="secondary-button" disabled={!validUrl || Boolean(busyRound)} onClick={() => void load(round, url.trim())}>{busyRound === round ? "Pobieram…" : "Odśwież z DP World"}</button>
                    <button type="button" className="text-button" onClick={() => clear(round)}>Wyczyść</button>
                  </div>
                </>
              )}

              <details className="dpwt-emergency">
                <summary>Generowanie awaryjne bez tee times</summary>
                <div className="dpwt-source-row">
                  <label className="dpwt-expected"><span>Liczba grup</span><input type="number" min={1} max={80} value={emergencyGroupCount} onChange={(event) => { const next = Math.max(1, Math.min(80, Number(event.target.value) || 1)); setEmergencyGroupCount(next); setEmergencyTee10Count(Math.min(Math.max(1, emergencyTee10Count), Math.max(1, next - 1))); }} /></label>
                  <label className="dpwt-expected"><span>Układ tee</span><select value={emergencyTwoTee ? "two" : "one"} onChange={(event) => setEmergencyTwoTee(event.target.value === "two")}><option value="two">Tee 10 + Tee 1</option><option value="one">Tylko Tee 1</option></select></label>
                  {emergencyTwoTee ? <label className="dpwt-expected"><span>Ile grup z Tee 10</span><input type="number" min={1} max={Math.max(1, emergencyGroupCount - 1)} value={emergencyTee10Count} onChange={(event) => setEmergencyTee10Count(Math.max(1, Math.min(Math.max(1, emergencyGroupCount - 1), Number(event.target.value) || 1)))} /></label> : null}
                  <label className="dpwt-expected"><span>Grupy</span><select value={emergencyPlayers} onChange={(event) => setEmergencyPlayers(Number(event.target.value) === 2 ? 2 : 3)}><option value={3}>3-osobowe</option><option value={2}>2-osobowe</option></select></label>
                  <div className="dpwt-source-actions"><button type="button" className="secondary-button" onClick={() => createEmergency(round)}>Utwórz szkielet {round}</button></div>
                </div>
                <small>Powstają normalne numery grup i tee, ale bez oficjalnych godzin. Generator ułoży scorerów na technicznych czasach niewidocznych dla operatora; transport będzie wyłączony do wczytania prawdziwego drawu.</small>
              </details>
              {network ? <div className="dpwt-network-diagnostic"><strong>Diagnostyka:</strong> {responseSummary(network)}</div> : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
